import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  Spinner,
  Text,
  Flex,
  Stack,
  Heading,
  useForm,
  FormProvider,
  TextInput,
  Button,
  useToast,
  SelectInput,
} from '@thirstycamel/ui'
import { GameEntity } from '@thirsty-camel/hump-club/src/modules/game/game.entity'
import { humpclub, RequestOptions } from '../../utils/backend'
import LayoutGame from '../../layout/LayoutGame'
import { useRouter } from 'next/router'
import { useQuery } from 'react-query'
import { useUser } from '../../hooks/useUser'
import { useActions, useStore } from '../../store/hooks'
import Input from '../../components/Input'
import { isAxiosError } from '../../utils/typeguards'

const GAME_BUCKET_BASE_URL = 'https://storage.googleapis.com/tc-games-cav-2025'

type CamelCardStore = {
  name: string
  merchantId: number
}

//hardcoded south australia store list as we didn't receive an endpoint from RDJ to pull this list dynamically
export const CAMEL_CARD_STORES: CamelCardStore[] = [
  { name: 'Woodcroft Hotel', merchantId: 2 },
  { name: 'Colonnades Tavern', merchantId: 3 },
  { name: 'The Cove Tavern', merchantId: 4 },
  { name: 'Settlers Hotel', merchantId: 5 },
  { name: 'The Gully Public House & Garden', merchantId: 6 },
  { name: 'Strathmore Hotel', merchantId: 15 },
  { name: 'Naracoorte Hotel/Motel', merchantId: 19 },
  { name: 'The Bartley Tavern', merchantId: 20 },
  { name: 'The Lodge', merchantId: 27 },
  { name: 'Highlander Hotel Motel', merchantId: 29 },
  { name: 'Belair Hotel', merchantId: 41 },
  { name: 'Goodwood Park Hotel', merchantId: 43 },
  { name: 'Crown Inn Reynella', merchantId: 51 },
  { name: 'Peninsula Hotel Motel', merchantId: 58 },
  { name: 'Midway Tavern', merchantId: 63 },
  { name: 'Cremorne Hotel Unley', merchantId: 64 },
  { name: 'Morphett Arms Hotel', merchantId: 71 },
  { name: 'Warradale Hotel', merchantId: 72 },
  { name: 'Albion Hotel', merchantId: 85 },
  { name: 'Modbury Hotel', merchantId: 86 },
  { name: 'Kingsford Hotel', merchantId: 87 },
  { name: 'The Aussie Inn', merchantId: 92 },
  { name: 'Sundowner Hotel Motel', merchantId: 94 },
  { name: 'The Yankalilla Hotel', merchantId: 95 },
  { name: 'Mawson Lakes Hotel', merchantId: 97 },
  { name: 'Highway Inn', merchantId: 102 },
  { name: 'Largs Pier Hotel-Motel', merchantId: 103 },
  { name: 'Goolwa Hotel', merchantId: 121 },
  { name: 'Risdon Hotel', merchantId: 129 },
  { name: 'Commercial Hotel', merchantId: 132 },
  { name: 'Woolshed Hotel', merchantId: 133 },
  { name: 'Loxton Hotel', merchantId: 134 },
  { name: 'Hilton Hotel', merchantId: 150 },
  { name: 'Clovercrest Hotel', merchantId: 152 },
  { name: 'Port Broughton Hotel', merchantId: 154 },
  { name: 'Hibiscus Tavern', merchantId: 157 },
  { name: 'Christies Beach Hotel', merchantId: 165 },
  { name: 'Whitehorse Inn', merchantId: 174 },
  { name: 'Headframe Bottleshop', merchantId: 175 },
  { name: 'Hotel Mclaren', merchantId: 179 },
  { name: 'The Marryatville Hotel', merchantId: 183 },
  { name: 'Minlaton Hotel Motel', merchantId: 187 },
  { name: 'Cross Keys Hotel', merchantId: 190 },
  { name: 'Oakbank Hotel', merchantId: 194 },
  { name: 'Lizards Bar & Restaurant', merchantId: 195 },
  { name: 'The Kincraig Hotel', merchantId: 196 },
]

type GameMobileNumberOnlyLoginResponse = {
  userId: string
  sessionToken: string
  system: string
  createdNewUser?: boolean
}

const getGameMobileLoginErrorCopy = (err: unknown) => {
  if (isAxiosError(err)) {
    const status = err.response?.status

    if (status === 400) {
      return {
        title: 'Invalid mobile number',
        description: 'Please enter a valid Australian mobile number.',
      }
    }

    if (status === 503) {
      return {
        title: 'Service unavailable',
        description:
          'We’re having trouble verifying memberships right now. Please try again later.',
      }
    }
  }

  return {
    title: 'Something went wrong',
    description: 'Please try again later.',
  }
}

type CamelCardRegistrationApiResponse = {
  ok: boolean
  message?: string
  errorType?: number
}

const Game: FC = () => {
  const router = useRouter()

  const slug = router.query.slug as string
  const _authKey = router.query.authKey as string
  const { isLoggedIn, isWithin14DaysAfterBirthday } = useUser()
  const [authKey, setAuthKey] = useState(_authKey)
  const [gameToken, setGameToken] = useState<string | null>(null)
  const [sessionToken, setSessionToken] = useState<string | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [gameStep, setGameStep] = useState<'landing' | 'select-state'>('landing')
  const [gameLoginError, setGameLoginError] = useState<string | null>(null)
  const [signupError, setSignupError] = useState<string | null>(null)
  const [isGameLoginLoading, setIsGameLoginLoading] = useState(false)
  const [isHumpClubSignupLoading, setIsHumpClubSignupLoading] = useState(false)
  const [isCamelCardSignupLoading, setIsCamelCardSignupLoading] = useState(false)
  const [showHumpClubSignup, setShowHumpClubSignup] = useState(false)
  const [showCamelCardSignup, setShowCamelCardSignup] = useState(false)

  const { loginWithAuthKey, getAuthKey } = useActions(actions => actions.auth)
  const { isFetching: isFetchingUser, isFetchingAuthKey, token } = useStore(store => store.auth)
  const toast = useToast()
  const isCreatingSessionRef = useRef(false)
  const gameLoginForm = useForm<{ mobileNumber: string }>()
  const humpClubSignupForm = useForm<{ mobileNumber: string }>()

  const camelCardSignupForm = useForm<{
    mobileNumber: string
    storeId: string
  }>()

  const { isFetching, error, data } = useQuery<GameEntity, [string, RequestOptions<any>?]>(
    [`/games/slug/${slug}`],
    humpclub,
  )

  const isBirthdayGame = !!data?.isBirthdayGame || data?.slug === 'birthday'
  const isCamelCashGame = !!data?.isCamelCashGame
  // For mobile-flow games, allow existing site login to bypass mobile-only flow.
  // This preserves legacy behavior for logged-in users while enabling mobile-only access.
  const shouldUseSiteAuth = !isBirthdayGame && isLoggedIn && token
  const isIneligibleToPlayGame = isBirthdayGame && !isWithin14DaysAfterBirthday

  useEffect(() => {
    setGameToken(null)
    setSessionError(null)
    setSessionToken(null)
    setGameLoginError(null)
    setSignupError(null)
    setGameStep('landing')
    setShowHumpClubSignup(false)
    setShowCamelCardSignup(false)
    isCreatingSessionRef.current = false
  }, [data?.id])

  const fetchAuthKey = async () => {
    const key = await getAuthKey()

    if (typeof key === 'string') {
      setAuthKey(key)
    }
  }

  useEffect(() => {
    const authToken = shouldUseSiteAuth ? token : !isBirthdayGame ? sessionToken : token

    if (!data?.id || !authToken || !!gameToken || !!sessionError || isCreatingSessionRef.current) {
      return
    }

    if (isIneligibleToPlayGame) {
      setTimeout(() => {
        router.push('/account/offers')
      }, 3000)

      return
    }

    const createSession = async () => {
      isCreatingSessionRef.current = true
      try {
        const sessionEndpoint =
          !isBirthdayGame && !shouldUseSiteAuth
            ? `/games/${data.id}/sessions/camel-cash-game`
            : `/games/${data.id}/sessions`
        const response = await humpclub<{ token: string }>(sessionEndpoint, {
          method: 'POST',
          data: {},
          headers: { Authorization: `Bearer ${authToken}` },
        })

        setGameToken(response.token)
      } catch (err) {
        console.error('Failed to start game session', err)
        const forbiddenMessage =
          isAxiosError(err) && err.response?.status === 403
            ? (err.response.data?.message as string | undefined)
            : undefined
        setSessionError(forbiddenMessage || 'Unable to start game session. Please try again later.')
      } finally {
        isCreatingSessionRef.current = false
      }
    }

    createSession()
  }, [
    data?.id,
    token,
    sessionToken,
    gameToken,
    sessionError,
    isIneligibleToPlayGame,
    isBirthdayGame,
    shouldUseSiteAuth,
  ])

  useEffect(() => {
    if (!isBirthdayGame) {
      return
    }

    if (authKey) {
      loginWithAuthKey({ authKey })
    } else if (isLoggedIn) {
      fetchAuthKey()
    }
  }, [authKey, isBirthdayGame])

  const handleGameLogin = gameLoginForm.handleSubmit(async values => {
    setIsGameLoginLoading(true)
    setGameLoginError(null)
    setSessionError(null)

    try {
      const response = await humpclub<
        GameMobileNumberOnlyLoginResponse,
        { mobileNumber: string; lookupOnly: boolean; gameId?: string }
      >('auth/login/game-mobile-number-only-login', {
        method: 'post',
        data: { mobileNumber: values.mobileNumber, lookupOnly: true, gameId: data?.id },
      })

      setSessionToken(response.sessionToken)
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        toast({
          title: 'Mobile number not found',
          description: "Please choose 'Not a member' to sign up",
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      } else {
        const { title, description } = getGameMobileLoginErrorCopy(err)
        toast({
          title,
          description,
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      }
    } finally {
      setIsGameLoginLoading(false)
    }
  })

  const handleHumpClubSignup = humpClubSignupForm.handleSubmit(async values => {
    setIsHumpClubSignupLoading(true)
    setSignupError(null)
    setSessionError(null)

    try {
      const response = await humpclub<
        GameMobileNumberOnlyLoginResponse,
        { mobileNumber: string; gameId?: string }
      >('auth/login/game-mobile-number-only-login', {
        method: 'post',
        data: { mobileNumber: values.mobileNumber, gameId: data?.id },
      })

      setSessionToken(response.sessionToken)

      if (response.createdNewUser) {
        toast({
          title: 'You’ve been signed up to Hump Club!',
          description: 'We’ve sent you an SMS with steps to complete your signup.',
          status: 'success',
          duration: 6000,
          isClosable: true,
        })
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        toast({
          title: 'Mobile number not found',
          description: "Please choose 'Not a member' to sign up",
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      } else {
        const { title, description } = getGameMobileLoginErrorCopy(err)
        toast({
          title,
          description,
          status: 'error',
          duration: 6000,
          isClosable: true,
        })
      }
    } finally {
      setIsHumpClubSignupLoading(false)
    }
  })

  const handleCamelCardSignup = camelCardSignupForm.handleSubmit(
    async values => {
      setIsCamelCardSignupLoading(true)
      setSignupError(null)
      setSessionError(null)

      try {
        const selectedStore = CAMEL_CARD_STORES.find(
          store => String(store.merchantId) === values.storeId,
        )

        if (!selectedStore) {
          toast({
            title: 'Please select a store',
            description: 'Choose a store to continue.',
            status: 'error',
            duration: 6000,
            isClosable: true,
          })
          return
        }

        let registrationResponse: CamelCardRegistrationApiResponse | null = null

        try {
          registrationResponse = await humpclub<
            CamelCardRegistrationApiResponse,
            { phoneNumber: string; merchantId: number }
          >('camelcard/register', {
            method: 'post',
            data: { phoneNumber: values.mobileNumber, merchantId: selectedStore.merchantId },
          })
        } catch (err) {
          const { title, description } = getGameMobileLoginErrorCopy(err)
          toast({
            title,
            description,
            status: 'error',
            duration: 6000,
            isClosable: true,
          })
          return
        }

        if (!registrationResponse?.ok) {
          toast({
            title: 'Registration failed',
            description:
              registrationResponse?.message ||
              'Unable to register right now. Please try again later.',
            status: 'error',
            duration: 6000,
            isClosable: true,
          })
          return
        }

        try {
          const response = await humpclub<
            GameMobileNumberOnlyLoginResponse,
            { mobileNumber: string; gameId?: string }
          >('auth/login/game-mobile-number-only-login', {
            method: 'post',
            data: { mobileNumber: values.mobileNumber, gameId: data?.id },
          })

          setSessionToken(response.sessionToken)
        } catch (err) {
          const { title, description } = getGameMobileLoginErrorCopy(err)
          toast({
            title,
            description,
            status: 'error',
            duration: 6000,
            isClosable: true,
          })
        }
      } finally {
        setIsCamelCardSignupLoading(false)
      }
    },
    errors => {
      let title = 'Invalid form'
      let message = 'Please check your entries and try again.'

      if (errors?.mobileNumber?.message) {
        title = 'Invalid mobile number'
        message = errors.mobileNumber.message
      } else if (errors?.storeId?.message) {
        title = 'Store required'
        message = errors.storeId.message || 'Please select a store to continue.'
      }

      toast({
        title,
        description: message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      })
    },
  )

  const requireAuth =
    !isFetching &&
    !!data?.id &&
    isBirthdayGame &&
    !(isFetchingUser || isFetchingAuthKey) &&
    !(isLoggedIn || authKey)

  const baseGameUrl = useMemo(() => {
    if (!data?.gamePath) {
      return null
    }

    const trimmedPath = data.gamePath.replace(/\/+$/, '')
    const hasExtension = /\.[a-z0-9]+$/i.test(trimmedPath)
    const resolvedPath = hasExtension ? trimmedPath : `${trimmedPath}/index.html`

    return `${GAME_BUCKET_BASE_URL}/${resolvedPath}`
  }, [data?.gamePath])

  const url = useMemo(() => {
    if (!baseGameUrl || !gameToken) {
      return null
    }

    const separator = baseGameUrl.includes('?') ? '&' : '?'
    return `${baseGameUrl}${separator}token=${encodeURIComponent(gameToken)}`
  }, [baseGameUrl, gameToken])

  const gameSlug = data?.slug || slug
  const hasRedButtons = gameSlug === 'save-the-egg' || gameSlug === 'fast-lane'
  const actionButtonColor = hasRedButtons ? 'red' : 'green'

  // Render mobile signup flow/background for mobile-flow games.
  // Guard on loaded game data: until it arrives isBirthdayGame is falsy, and a
  // login submitted in that window would omit gameId and default to camelCard: true.
  const showCamelCardGameFlow =
    !!data?.id && !isBirthdayGame && !shouldUseSiteAuth && !sessionToken
  const isLoading = isFetching || !url
  const gameBackgroundAspectRatio = '2250 / 4000'
  //name background images as per slug and below to dynamically display
  const gameBackground =
    gameStep === 'landing' || !isCamelCashGame
      ? `/assets/game/${gameSlug}-landing.jpg`
      : `/assets/game/${gameSlug}-select-state.jpg`

  const handleExternalSignup = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <LayoutGame
      title={isFetching ? 'Loading Game...' : data?.title}
      containerSize="sm"
      bg="gray.50"
      requireAuth={requireAuth}
      redirectTo={`/games/${slug}`}
    >
      {showCamelCardGameFlow ? (
        <Stack p={0} justifyContent="center" alignItems="center" position="relative" zIndex={1}>
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width={`min(calc(100dvh * ${gameBackgroundAspectRatio}), 100vw)`}
            height="100dvh"
            overflow="hidden"
            maxWidth={'100vw'}
            style={{ scrollSnapAlign: 'end', aspectRatio: gameBackgroundAspectRatio }}
          >
            <Stack
              spacing={4}
              alignItems={'center'}
              justifyContent="center"
              padding={0}
              height="100%"
              width="100%"
              position="relative"
              p={8}
              overflow="hidden"
              backgroundImage={`url(${gameBackground})`}
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              backgroundSize="100% 100%"
            >
              <Stack
                spacing={4}
                alignItems={'center'}
                justifyContent="flex-start"
                position="absolute"
                zIndex={1}
                top="58%"
                bottom="8%"
                left="50%"
                width="76%"
                maxWidth="420px"
                transform="translateX(-50%)"
                overflowY="auto"
              >
                {gameStep === 'landing' ? (
                  <>
                    <FormProvider {...gameLoginForm}>
                      <Stack as="form" spacing={4} onSubmit={handleGameLogin} width="100%">
                        <Input
                          as={TextInput}
                          isRequired
                          name="mobileNumber"
                          type="tel"
                          placeholder="MOBILE NUMBER"
                          _placeholder={{ fontFamily: 'heading' }}
                          isReadOnly={isGameLoginLoading}
                          backgroundColor="white"
                          _readOnly={{ backgroundColor: 'white' }}
                          onChangeValue={() => setGameLoginError(null)}
                        />

                        <Button
                          type="submit"
                          variantColor={actionButtonColor}
                          w="100%"
                          isLoading={isGameLoginLoading}
                        >
                          Play Game
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          variantColor={actionButtonColor}
                          w="100%"
                          onClick={() => {
                            setGameStep('select-state')
                            if (!isCamelCashGame) {
                              setShowHumpClubSignup(true)
                            }
                            setGameLoginError(null)
                          }}
                        >
                          Not a member?
                        </Button>
                      </Stack>
                    </FormProvider>
                  </>
                ) : (
                  <>
                    {!showHumpClubSignup && !showCamelCardSignup ? (
                      <Stack spacing={3} width="100%">
                        <Button
                          variantColor={actionButtonColor}
                          w="100%"
                          onClick={() => {
                            setShowHumpClubSignup(true)
                            setShowCamelCardSignup(false)
                          }}
                        >
                          VIC, NSW, QLD, TAS
                        </Button>
                        <Button
                          // variant="outline"
                          variantColor={actionButtonColor}
                          w="100%"
                          onClick={() => {
                            setShowCamelCardSignup(true)
                            setShowHumpClubSignup(false)
                          }}
                        >
                          SA
                        </Button>
                        <Button
                          // variant="outline"
                          variantColor={actionButtonColor}
                          w="100%"
                          onClick={() =>
                            handleExternalSignup(
                              'https://www.cardholder.com.au/Register/RegisterMe',
                            )
                          }
                        >
                          NT
                        </Button>
                      </Stack>
                    ) : showCamelCardSignup ? (
                      <FormProvider {...camelCardSignupForm}>
                        <Stack as="form" spacing={4} onSubmit={handleCamelCardSignup} width="100%">
                          <Input
                            as={TextInput}
                            isRequired
                            name="mobileNumber"
                            type="tel"
                            placeholder="MOBILE NUMBER"
                            _placeholder={{ fontFamily: 'heading' }}
                            isReadOnly={isCamelCardSignupLoading}
                            backgroundColor="white"
                            _readOnly={{ backgroundColor: 'white' }}
                            onChangeValue={() => setSignupError(null)}
                          />

                          <Input
                            as={SelectInput}
                            isRequired
                            name="storeId"
                            placeholder="Select a store..."
                            options={CAMEL_CARD_STORES.map(store => ({
                              label: store.name,
                              value: String(store.merchantId),
                            }))}
                          />

                          <Button
                            type="submit"
                            variantColor={actionButtonColor}
                            w="100%"
                            isLoading={isCamelCardSignupLoading}
                          >
                            Join & Play
                          </Button>

                          <Button
                            type="button"
                            variant="outline"
                            variantColor={actionButtonColor}
                            w="100%"
                            onClick={() => {
                              setShowCamelCardSignup(false)
                            }}
                          >
                            Change state
                          </Button>
                        </Stack>
                      </FormProvider>
                    ) : (
                      <FormProvider {...humpClubSignupForm}>
                        <Stack as="form" spacing={4} onSubmit={handleHumpClubSignup} width="100%">
                          <Input
                            as={TextInput}
                            isRequired
                            name="mobileNumber"
                            type="tel"
                            placeholder="MOBILE NUMBER"
                            _placeholder={{ fontFamily: 'heading' }}
                            isReadOnly={isHumpClubSignupLoading}
                            backgroundColor="white"
                            _readOnly={{ backgroundColor: 'white' }}
                            onChangeValue={() => setSignupError(null)}
                          />

                          <Button
                            type="submit"
                            variantColor={actionButtonColor}
                            w="100%"
                            isLoading={isHumpClubSignupLoading}
                          >
                            Join & Play
                          </Button>

                          {isCamelCashGame && (
                            <Button
                              type="button"
                              variant="outline"
                              variantColor={actionButtonColor}
                              w="100%"
                              onClick={() => setShowHumpClubSignup(false)}
                            >
                              Change state
                            </Button>
                          )}

                          {!isCamelCashGame && (
                            <Text color="white" textAlign="center" fontSize="sm">
                              *Offer only available at Thirsty Camel VIC and NSW border stores.
                            </Text>
                          )}
                        </Stack>
                      </FormProvider>
                    )}

                    <Text
                      color="white"
                      textAlign="center"
                      cursor="pointer"
                      fontFamily="heading"
                      fontWeight="medium"
                      fontSize="lg"
                      mt="15px"
                      onClick={() => {
                        setGameStep('landing')
                        setShowHumpClubSignup(false)
                        setShowCamelCardSignup(false)
                        setSignupError(null)
                      }}
                    >
                      BACK TO SIGN IN
                    </Text>
                  </>
                )}
              </Stack>
            </Stack>
          </Flex>
        </Stack>
      ) : isLoading || isIneligibleToPlayGame ? (
        <Stack p={5} justifyContent="center" alignItems="center">
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            width="560px"
            height="990px"
            overflow="hidden"
            maxHeight={['100dvh']}
            maxWidth={'100vw'}
            p={2}
            style={{ scrollSnapAlign: 'end' }}
          >
            <Stack
              spacing={4}
              alignItems={'center'}
              justifyContent="center"
              padding={0}
              height="100%"
              width="100%"
              backgroundColor={'white'}
              p={8}
            >
              {sessionError ? (
                <Stack spacing={4} alignItems="center">
                  <Text fontWeight="bold" fontSize="lg" textAlign="center">
                    {sessionError}
                  </Text>
                  {!isBirthdayGame && !shouldUseSiteAuth && (
                    <Button
                      variantColor={actionButtonColor}
                      onClick={() => {
                        setSessionError(null)
                        setSessionToken(null)
                        setGameToken(null)
                        setGameStep('landing')
                        setShowHumpClubSignup(false)
                        setShowCamelCardSignup(false)
                      }}
                    >
                      Try another mobile
                    </Button>
                  )}
                </Stack>
              ) : (
                <Stack spacing={2} alignItems="center">
                  <Spinner color="pink.500" size="xl" />

                  <Text fontWeight={'bold'} fontSize={'lg'} textAlign={'center'}>
                    {isIneligibleToPlayGame
                      ? 'Whoops! You are not eligible to play at this time, redirecting to account page...'
                      : !isBirthdayGame
                      ? 'Verifying your mobile number...'
                      : 'Logging into your account...'}
                  </Text>
                </Stack>
              )}
            </Stack>
          </Flex>
        </Stack>
      ) : (
        <Stack p={5} justifyContent="center" alignItems="center">
          <Stack
            spacing={3}
            alignItems="center"
            mb={4}
            style={{ scrollSnapAlign: 'end' }}
            display={['none']}
          >
            <Heading textAlign={'center'} mb={4} fontSize={24}>
              {data?.title?.toUpperCase()}
            </Heading>

            {data?.description && (
              <Text textAlign={'center'} color="pink.500" fontSize={14} fontWeight="bold">
                {data.description?.toUpperCase()}
              </Text>
            )}
          </Stack>

          <Flex
            flexDirection="column"
            alignItems="center"
            width={'100vw'}
            height={'100vh'}
            overflow="hidden"
            maxHeight={['100dvh']}
            maxWidth={'100vw'}
            p={[0, 2]}
            style={{ scrollSnapAlign: 'end' }}
            position={['fixed']}
            top={[0]}
            left={[0]}
          >
            <iframe
              src={url || undefined}
              title={data?.title}
              height="100%"
              width="100%"
              style={{ backgroundColor: 'black' }}
            />
          </Flex>
        </Stack>
      )}
    </LayoutGame>
  )
}

export default memo(Game)
